import { useState, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import type { ClubTheme } from './themes';
import type { SocialAnnotation } from './types';
import AnnotationPopover from './AnnotationPopover';

interface AnnotatableTextProps {
  text: string;
  annotations: SocialAnnotation[];
  theme: ClubTheme;
  isSystem?: boolean;
  clubId: string;
  postId: string;
  onAnnotate?: (selection: { start: number; end: number; text: string; type: string }) => void;
  onFactCheck?: (selection: { start: number; end: number; text: string }) => void;
  onStartDiscussion?: (annotationId: string) => void;
}

const ANNOTATION_COLORS: Record<string, string> = {
  note: '#FBBF2440',
  'fact-check': '#10B98130',
  question: '#3B82F630',
};

interface TextSegment {
  start: number;
  end: number;
  text: string;
  annotations: SocialAnnotation[];
}

/** Build segments from text + annotations. Overlapping annotations stack. */
function buildSegments(text: string, annotations: SocialAnnotation[]): TextSegment[] {
  if (annotations.length === 0) {
    return [{ start: 0, end: text.length, text, annotations: [] }];
  }

  // Collect all boundary points
  const boundaries = new Set<number>();
  boundaries.add(0);
  boundaries.add(text.length);
  for (const a of annotations) {
    boundaries.add(Math.max(0, a.start_offset));
    boundaries.add(Math.min(text.length, a.end_offset));
  }

  const sorted = Array.from(boundaries).sort((a, b) => a - b);
  const segments: TextSegment[] = [];

  for (let i = 0; i < sorted.length - 1; i++) {
    const start = sorted[i];
    const end = sorted[i + 1];
    const overlapping = annotations.filter(
      (a) => a.start_offset <= start && a.end_offset >= end
    );
    segments.push({
      start,
      end,
      text: text.slice(start, end),
      annotations: overlapping,
    });
  }

  return segments;
}

export default function AnnotatableText({
  text,
  annotations,
  theme,
  isSystem,
  clubId,
  postId,
  onAnnotate,
  onFactCheck,
  onStartDiscussion,
}: AnnotatableTextProps) {
  const containerRef = useRef<HTMLParagraphElement>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);
  const [hoveredAnnotation, setHoveredAnnotation] = useState<SocialAnnotation | null>(null);
  const [popoverPos, setPopoverPos] = useState<{ x: number; y: number } | null>(null);
  const [selectionToolbar, setSelectionToolbar] = useState<{
    x: number;
    y: number;
    start: number;
    end: number;
    text: string;
  } | null>(null);

  const segments = buildSegments(text, annotations);

  const handleMouseUp = useCallback(() => {
    const selection = window.getSelection();
    if (!selection || selection.isCollapsed || !containerRef.current) {
      return;
    }

    const range = selection.getRangeAt(0);
    const selectedText = selection.toString().trim();
    if (!selectedText) return;

    // Calculate character offsets relative to the text
    const container = containerRef.current;
    const preRange = document.createRange();
    preRange.setStart(container, 0);
    preRange.setEnd(range.startContainer, range.startOffset);
    const startOffset = preRange.toString().length;
    const endOffset = startOffset + selectedText.length;

    const rect = range.getBoundingClientRect();

    // Use viewport coordinates (for portal rendering) to avoid clipping by overflow parents
    setSelectionToolbar({
      x: rect.left + rect.width / 2,
      y: rect.top - 8,
      start: startOffset,
      end: endOffset,
      text: selectedText,
    });
  }, []);

  // Dismiss toolbar on click outside (but not on the toolbar itself)
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (!selectionToolbar) return;
      const target = e.target as Node;
      if (containerRef.current?.contains(target)) return;
      if (toolbarRef.current?.contains(target)) return;
      setSelectionToolbar(null);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [selectionToolbar]);

  const handleAnnotationHover = (annotation: SocialAnnotation, e: React.MouseEvent) => {
    const rect = (e.target as HTMLElement).getBoundingClientRect();
    setHoveredAnnotation(annotation);
    setPopoverPos({
      x: rect.left + rect.width / 2,
      y: rect.bottom + 4,
    });
  };

  return (
    <div className="relative">
      <p
        ref={containerRef}
        onMouseUp={handleMouseUp}
        className={`mt-3 whitespace-pre-wrap leading-relaxed ${isSystem ? 'text-sm font-medium' : 'text-sm'}`}
        style={{ color: theme.colors.text }}
      >
        {segments.map((seg, i) => {
          if (seg.annotations.length === 0) {
            return <span key={i}>{seg.text}</span>;
          }

          // Use the first annotation's type for color
          const primary = seg.annotations[0];
          const bgColor = ANNOTATION_COLORS[primary.annotation_type] || ANNOTATION_COLORS.note;

          return (
            <mark
              key={i}
              className="cursor-pointer rounded-sm px-0.5 transition-opacity hover:opacity-80"
              style={{ backgroundColor: bgColor, color: 'inherit' }}
              onMouseEnter={(e) => handleAnnotationHover(primary, e)}
              onMouseLeave={() => { setHoveredAnnotation(null); setPopoverPos(null); }}
            >
              {seg.text}
            </mark>
          );
        })}
      </p>

      {/* Selection toolbar — rendered via portal to avoid overflow clipping */}
      {selectionToolbar && (onAnnotate || onFactCheck) && createPortal(
        <div
          ref={toolbarRef}
          className="fixed z-[9999] flex gap-1 rounded-lg p-1 shadow-lg"
          style={{
            left: selectionToolbar.x,
            top: selectionToolbar.y,
            transform: 'translate(-50%, -100%)',
            backgroundColor: theme.colors.surface,
            border: `1px solid ${theme.colors.muted}30`,
          }}
        >
          {onAnnotate && (
            <button
              type="button"
              onClick={() => {
                onAnnotate({ start: selectionToolbar.start, end: selectionToolbar.end, text: selectionToolbar.text, type: 'note' });
                setSelectionToolbar(null);
                window.getSelection()?.removeAllRanges();
              }}
              className="rounded px-2 py-1 text-[10px] font-semibold transition-colors hover:opacity-80"
              style={{ color: '#F59E0B', backgroundColor: '#F59E0B15' }}
            >
              📝 Note
            </button>
          )}
          {onAnnotate && (
            <button
              type="button"
              onClick={() => {
                onAnnotate({ start: selectionToolbar.start, end: selectionToolbar.end, text: selectionToolbar.text, type: 'question' });
                setSelectionToolbar(null);
                window.getSelection()?.removeAllRanges();
              }}
              className="rounded px-2 py-1 text-[10px] font-semibold transition-colors hover:opacity-80"
              style={{ color: '#3B82F6', backgroundColor: '#3B82F615' }}
            >
              ❓ Question
            </button>
          )}
          {onFactCheck && (
            <button
              type="button"
              onClick={() => {
                onFactCheck({ start: selectionToolbar.start, end: selectionToolbar.end, text: selectionToolbar.text });
                setSelectionToolbar(null);
                window.getSelection()?.removeAllRanges();
              }}
              className="rounded px-2 py-1 text-[10px] font-semibold transition-colors hover:opacity-80"
              style={{ color: '#10B981', backgroundColor: '#10B98115' }}
            >
              🛡️ Verify
            </button>
          )}
        </div>,
        document.body,
      )}

      {/* Annotation popover — portal to avoid overflow clipping */}
      {hoveredAnnotation && popoverPos && createPortal(
        <div
          className="fixed z-[9999]"
          style={{ left: popoverPos.x, top: popoverPos.y, transform: 'translateX(-50%)' }}
        >
          <AnnotationPopover
            annotation={hoveredAnnotation}
            theme={theme}
            onStartDiscussion={onStartDiscussion}
          />
        </div>,
        document.body,
      )}
    </div>
  );
}
